import { useCallback, useEffect, useState, type FormEvent } from "react";

import {
  fetchNotificationSettings,
  parseDays,
  saveNotificationSettings,
  type NotificationSettings,
} from "../lib/notificationSettings";

/**
 * Секция «Настроек» — параметры уведомлений и эскалации (E10, Приложение В ТЗ).
 *
 * Только администратор: сервер требует право `settings:manage` на каждом маршруте (строка 4.3
 * «Настройки, справочники…», ТЗ 4.2.4 «правила уведомлений настраиваются администратором»), а
 * сама страница «Настройки» видна лишь ему. Запрет серверный, видимость раздела — подсказка.
 *
 * Все пороги — в КАЛЕНДАРНЫХ днях (Р-32: ТЗ тип дней не задаёт, принято календарное исчисление).
 * Оформление — токены docs/07 (правило 9), тексты русские (правило 8).
 */

const btnPrimary =
  "rounded-[var(--radius-m)] bg-accent px-3 py-1.5 text-[13px] font-bold text-surface disabled:opacity-50";
const inputCls =
  "w-full rounded-[var(--radius-m)] border border-border bg-surface px-2.5 py-1.5 text-[14px]";
const labelCls = "mb-1 block text-[12px] font-bold text-muted";

type Load =
  | { kind: "loading" }
  | { kind: "ready"; settings: NotificationSettings }
  | { kind: "error"; message: string };

/** Поля-пороги формы (строки — чтобы поле можно было очистить и набрать заново). */
interface DraftDays {
  escalationDays: string;
  notifyPayment: string;
  notifyDelivery: string;
  notifyOther: string;
}

const FIELDS: { key: keyof DraftDays; id: string; label: string; hint: string }[] = [
  {
    key: "notifyPayment",
    id: "ns-payment",
    label: "Оплата: предупредить за, дн.",
    hint: "Адресат — бухгалтерия (Приложение В ТЗ).",
  },
  {
    key: "notifyDelivery",
    id: "ns-delivery",
    label: "Поставка / приёмка: за, дн.",
    hint: "Адресат — коммерческий отдел.",
  },
  {
    key: "notifyOther",
    id: "ns-other",
    label: "Прочие типы: за, дн.",
    hint: "Адресат — назначенный ответственный.",
  },
  {
    key: "escalationDays",
    id: "ns-escalation",
    label: "Эскалация: просрочка более, дн.",
    hint: "Сигнал директору и юристу. По умолчанию — более 3 дней.",
  },
];

export function NotificationSettingsSection() {
  const [state, setState] = useState<Load>({ kind: "loading" });
  const [days, setDays] = useState<DraftDays>({
    escalationDays: "",
    notifyPayment: "",
    notifyDelivery: "",
    notifyOther: "",
  });
  const [affiliatedMute, setAffiliatedMute] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  /** Поля, забракованные последней проверкой, — их и подсвечиваем. */
  const [invalid, setInvalid] = useState<Set<keyof DraftDays>>(new Set());

  /** Любая правка снимает «Настройки сохранены»: иначе зелёная строка висит над несохранённым. */
  const touch = useCallback(() => {
    setSaved(false);
    setError(null);
  }, []);

  const applyToForm = useCallback((settings: NotificationSettings) => {
    setDays({
      escalationDays: String(settings.escalationDays),
      notifyPayment: String(settings.notifyPayment),
      notifyDelivery: String(settings.notifyDelivery),
      notifyOther: String(settings.notifyOther),
    });
    setAffiliatedMute(settings.affiliatedMute);
  }, []);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    const res = await fetchNotificationSettings();
    if (!res.ok) {
      setState({ kind: "error", message: res.message });
      return;
    }
    setState({ kind: "ready", settings: res.value });
    applyToForm(res.value);
  }, [applyToForm]);

  useEffect(() => {
    void load();
  }, [load]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaved(false);

    const parsed = {
      escalationDays: parseDays(days.escalationDays),
      notifyPayment: parseDays(days.notifyPayment),
      notifyDelivery: parseDays(days.notifyDelivery),
      notifyOther: parseDays(days.notifyOther),
    };
    const bad = (Object.keys(parsed) as (keyof DraftDays)[]).filter((k) => parsed[k] === null);
    setInvalid(new Set(bad));
    if (bad.length > 0) {
      // Называем ИМЕННО те поля, что не приняты: при четырёх порогах «где-то ошибка» — не помощь.
      const names = FIELDS.filter((f) => bad.includes(f.key))
        .map((f) => `«${f.label}»`)
        .join(", ");
      setError(`Порог — целое число дней от 0 до 365. Проверьте: ${names}.`);
      document.getElementById(FIELDS.find((f) => f.key === bad[0])!.id)?.focus();
      return;
    }

    setBusy(true);
    const res = await saveNotificationSettings({
      escalationDays: parsed.escalationDays!,
      notifyPayment: parsed.notifyPayment!,
      notifyDelivery: parsed.notifyDelivery!,
      notifyOther: parsed.notifyOther!,
      affiliatedMute,
    });
    setBusy(false);

    if (!res.ok) {
      setError(res.message);
      return;
    }
    setState({ kind: "ready", settings: res.value });
    applyToForm(res.value);
    setInvalid(new Set());
    setSaved(true);
  };

  return (
    <section aria-labelledby="ns-title" className="mt-10">
      <h2 id="ns-title" className="mb-1 text-[16px] font-bold text-ink">
        Уведомления и эскалация
      </h2>
      <p className="mb-3 text-[13px] text-muted">
        За сколько дней предупреждать о сроке и с какой просрочки поднимать эскалацию директору и
        юристу. Значения по умолчанию — из Приложения В ТЗ: «за 3 дня» и «просрочка более 3 дней».
        Дни календарные. Изменения применяются сразу и записываются в журнал аудита.
      </p>

      {state.kind === "loading" && (
        <p className="text-[13px] text-muted" role="status">
          Загрузка настроек…
        </p>
      )}

      {state.kind === "error" && (
        <p role="alert" className="text-[13px] text-overdue-fg">
          {state.message}
        </p>
      )}

      {state.kind === "ready" && (
        <form onSubmit={(e) => void onSubmit(e)} className="max-w-[var(--w-form)]">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {FIELDS.map((f) => (
              <div key={f.key}>
                <label htmlFor={f.id} className={labelCls}>
                  {f.label}
                </label>
                <input
                  id={f.id}
                  inputMode="numeric"
                  value={days[f.key]}
                  aria-invalid={invalid.has(f.key)}
                  aria-describedby={`${f.id}-hint`}
                  onChange={(e) => {
                    touch();
                    setDays((prev) => ({ ...prev, [f.key]: e.target.value }));
                  }}
                  className={`${inputCls} num ${invalid.has(f.key) ? "border-overdue-fg" : ""}`}
                />
                <p id={`${f.id}-hint`} className="mt-1 text-[11px] text-muted">
                  {f.hint}
                </p>
              </div>
            ))}
          </div>

          {/* В18 (ответ юриста клиента 19.07.2026 — «включать!»): по умолчанию приглушение
              ВЫКЛЮЧЕНО, сигналы по внутригрупповым договорам идут как для всех. */}
          <label className="mt-4 inline-flex items-start gap-2 text-[13px] text-ink">
            <input
              type="checkbox"
              checked={affiliatedMute}
              onChange={(e) => {
                touch();
                setAffiliatedMute(e.target.checked);
              }}
              className="mt-0.5 accent-accent"
            />
            <span>
              Не эскалировать и не формировать претензионные сигналы по аффилированным
              (внутригрупповым) контрагентам
            </span>
          </label>
          <p className="mt-1 text-[12px] text-muted">
            По умолчанию выключено — эскалации и претензионные сигналы по внутригрупповым договорам
            идут как для всех (В18). Контроль сроков и напоминания ответственным не приглушаются
            никогда, как и предупреждение об исковой давности. Включение видно юристу в самом центре
            уведомлений.
          </p>

          {error && (
            <p role="alert" className="mt-3 text-[13px] text-overdue-fg">
              {error}
            </p>
          )}
          {saved && (
            <p role="status" className="mt-3 text-[13px] text-done-fg">
              Настройки сохранены.
            </p>
          )}

          <button type="submit" disabled={busy} className={`${btnPrimary} mt-3`}>
            {busy ? "Сохранение…" : "Сохранить"}
          </button>
        </form>
      )}
    </section>
  );
}
